import { LitElement, css, html, nothing } from '@lit'

class NovaSearchResult extends LitElement {
  static properties = {
    noResults: { type: String },
    searchAgain: { type: String },
    searchData: { type: Object },
    searchTerm: { type: String },
    noSearchResult: { type: Boolean },
    suggestedResult: { type: Object },
    openState: { type: String },
  }

  connectedCallback() {
    super.connectedCallback()
    this.searchData = {}
    this.searchTerm = ''
    this.noSearchResult = false
    this.suggestedResult = null

    window.addEventListener('resize', this.handleRenderHeight.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('resize', this.handleRenderHeight.bind(this))
  }

  updated(changedProperties) {
    super.updated(changedProperties)
    if (this.openState === 'closed') return
    if (Reflect.ownKeys(this.searchData).length === 0) return
    const { products, collections, articles, pages } =
      this.searchData.resources.results
    this.noSearchResult = [products, collections].every(
      (arr) => arr.length === 0,
    )

    if (this.suggestedResult === null && this.noSearchResult) {
      this.handleResult()
    } else if (this.noSearchResult) {
      this.handleRenderResult(this.suggestedResult)
    }

    this.handleRenderHeight()
  }

  async handleResult() {
    try {
      const response = await fetch(
        `?section_id=${window.main_suggested_result_section_id}`,
      )
      if (!response.ok) {
        console.error(`HTTP error! Status: ${response.status}`)
        console.error(`HTTP error! Reason: ${response}`)
      }
      const data = await response.text()
      const parser = new DOMParser()
      const htmlDocument = parser.parseFromString(data, 'text/html')
      this.suggestedResult = htmlDocument
      this.handleRenderResult(this.suggestedResult)
    } catch (error) {
      console.error('Error:', error.message)
    }
  }

  handleRenderHeight() {
    if (this.openState === 'closed') return

    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      const resultRec = this.getBoundingClientRect()
      if (window.innerHeight - resultRec.bottom > 0) {
        this.style.maxHeight = 'unset'
      } else {
        this.style.maxHeight = `${window.innerHeight - resultRec.top}px`
      }
    }, 500)
  }

  handleRenderResult(data) {
    const suggestedResult = data.querySelector('.suggested-results')
    if (!suggestedResult) return

    const listContainer = suggestedResult.querySelector(
      '.header-search__list-container',
    )
    const currentListContainers = this.shadowRoot.querySelectorAll(
      '.header-search__list-container',
    )
    if (listContainer && currentListContainers.length) {
      for (const currentListContainer of currentListContainers) {
        currentListContainer.replaceWith(listContainer.cloneNode(true))
      }
    }

    const productContainer = suggestedResult.querySelector(
      '.header-search__product-container',
    )
    const currentProductContainer = this.shadowRoot.querySelector(
      '.header-search__product-container',
    )
    if (productContainer && currentProductContainer) {
      currentProductContainer.innerHTML = productContainer.innerHTML

      const cards = currentProductContainer.querySelectorAll('nova-product-card')
      for (const card of cards) {
        const optionValuesTemplate = currentProductContainer.querySelector(
          `#async-options-json-${card.id}`,
        )
        const variantsTemplate = currentProductContainer.querySelector(
          `#async-variants-json-${card.id}`,
        )
        const productOptions = optionValuesTemplate
          ? JSON.parse(optionValuesTemplate.content.textContent.trim())
          : []
        const variants = variantsTemplate
          ? JSON.parse(variantsTemplate.content.textContent.trim())
          : []
        card.productOptions = productOptions.filter(
          (item) => item.name !== 'Title',
        )
        card.variants = variants
      }
    }
  }

  render() {
    if (Reflect.ownKeys(this.searchData).length === 0) return nothing
    if (this.noSearchResult) {
      return html`
        <div class="header-search__result header-search__no-result">
          <div class="header-search__no-result-container">
            <div class="header-search__no-result-content">
              <h3 class="header-search__no-result-title">
                ${this.noResults} "${this.searchTerm}"
              </h3>
              <p class="header-search__no-result-description">
                ${this.searchAgain}
              </p>
            </div>
            <div class="header-search__list-container"></div>
          </div>
          <div class="header-search__product-container"></div>
          <div class="header-search__list-container"></div>
        </div>
      `
    }
    const displayProducts = this.searchData.resources.results.products.slice(
      0,
      4,
    )
    return html`
        <div class="header-search__result">
          ${
            this.searchData.resources.results.products.length > 0
              ? html`<div class="header-search__product-container"
                    style="--card-counts: ${displayProducts.length};--space-counts: ${displayProducts.length - 1};"
                  >
                  <h3>${window.langMessages['general.search.products']}</h3>
                  <div class="header-search__product-slide-container">
                    <div class="header-search__product-slide-inner-container">
                      ${displayProducts.map(
                        (product) => html`
                          <div class="header-search__product-card">
                            <nova-product-card-async
                              data-handle=${product.handle}
                            ></nova-product-card-async>
                          </div>
                        `,
                      )}
                    </div>
                  </div>
                </div>`
              : nothing
          }
          <div class="header-search__list-container">
            ${
              this.searchData.resources.results.collections.length > 0
                ? html` <div class="header-search__collection-list">
                  <h3>${window.langMessages['general.search.collections']}</h3>
                  <ul>
                    ${this.searchData.resources.results.collections.slice(0, 3).map(
                      (collection) => html`
                        <li>
                          <a href=${collection.url}>${collection.title}</a>
                        </li>
                      `,
                    )}
                  </ul>
                </div>`
                : nothing
            }
            ${
              this.searchData.resources.results.articles?.length > 0
                ? html` <div class="header-search__article-list">
                  <h3>Blog</h3>
                  <ul>
                    ${this.searchData.resources.results.articles.map(
                      (article) => html`
                        <li><a href=${article.url}>${article.title}</a></li>
                      `,
                    )}
                  </ul>
                </div>`
                : nothing
            }
            ${
              this.searchData.resources.results.pages?.length > 0
                ? html` <div class="header-search__page-list">
                  <h3>Pages</h3>
                  <ul>
                    ${this.searchData.resources.results.pages.map(
                      (page) => html`
                        <li><a href=${page.url}>${page.title}</a></li>
                      `,
                    )}
                  </ul>
                </div>`
                : nothing
            }
          </div>
        </div>
      `
  }

  static styles = css`
    :host .header-search__result {
      display: flex;
      width: 100%;
      overflow: hidden;
      flex-direction: column;
      max-height: inherit;
      overflow-y: auto;
      -ms-overflow-style: none; 
      scrollbar-width: none;
      gap: var(--nova-space-lg);
      margin-top: var(--nova-space-lg);
    }

    :host .header-search__result::-webkit-scrollbar {
      display: none;
      width: 0; 
    }

    :host .header-search__product-container {
      width: 100%;
    }

    :host .header-search__list-container {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: var(--nova-space-md);
    }

    @media screen and (min-width: 1024px) {
      :host .header-search__result {
        flex-direction: row;
        justify-content: space-between;
      }

      :host .header-search__list-container {
        order: 1;
        width: fit-content;
        max-width: 180px;
        flex-shrink: 0;
        padding: var(--nova-space-none);
      }

      :host .header-search__product-container {
        order: 2;
        width: fit-content;
      }
    }

    :host .header-search__product-slide-container {
      overflow-x: auto;
      overflow-y: hidden;
      width: 100%;
      max-width: 100%;
      -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
      scrollbar-width: none; /* Hide scrollbar for Firefox */
    }

    /* Hide scrollbar for Chrome, Safari and Opera */
    :host .header-search__product-slide-container::-webkit-scrollbar {
      display: none;
      width: 0;
    }

    :host .header-search__product-slide-inner-container {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      width: 100%;
      gap: var(--nova-space-sm);
    }
  
    @media screen and (min-width: 750px) {
      :host .header-search__product-slide-container {
        padding: 0 var(--nova-space-2xs);
        width: calc(100% - 2 * var(--nova-space-2xs));
      }    
      :host .header-search__product-slide-inner-container {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
      }
    }
  
    @media screen and (min-width: 1024px) {
      :host .header-search__product-slide-inner-container {
        width: calc(100vw - 3 * var(--nova-space-page-margin) - 180px);
        max-width: 125rem;
        display: grid;
        grid-template-columns: repeat(4, 1fr);
      }
    }

    @media screen and (min-width: 1200px) {
      :host .header-search__list-container {
        max-width: 220px;
      }
      :host .header-search__product-slide-inner-container {
        width: calc(100vw - 3 * var(--nova-space-page-margin) - 220px);
      }
    }

    @media screen and (min-width: 1800px) {
      :host .header-search__list-container {
        max-width: 300px;
      }
      :host .header-search__product-slide-inner-container {
        width: calc(100vw - 3 * var(--nova-space-page-margin) - 450px);
      }
    }

    :host h3 {
      font: var(--nova-h8-font);
      text-transform: uppercase;
      margin: 0 0 var(--nova-space-md);
    }

    @media screen and (min-width: 768px) {
      :host h3 {
        margin: 0 0 var(--nova-space-sm);
      }
    }

    :host a {
      color: inherit;
      text-decoration: none;
    }

    :host ul {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-md);
      padding-left: var(--nova-space-none);
      margin: var(--nova-space-none);
    }

    @media screen and (min-width: 768px) {
      :host ul {
        gap: var(--nova-space-sm);
      }
    }

    :host li {
      list-style: none;
      color: var(--nova-color-text);
    }

    :host .header-search__no-result-container {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-lg);
    }
    :host .header-search__no-result-title {
      font: var(--nova-h7-font);
      margin: 0;
    }
    :host .header-search__no-result-description {
      margin: 0;
      padding: 0 var(--nova-space-page-margin);
    }
    :host .header-search__no-result-content {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-xs);
    }
    :host .header-search__no-result-container > .header-search__list-container {
      display: none;
    }
    @media screen and (min-width: 750px) {
      :host .header-search__no-result-description {
        padding: 0;
      }
    }
    @media screen and (min-width: 768px) {
      :host .header-search__no-result-container {
        gap: var(--nova-space-md);
      }
    }
    @media screen and (min-width: 1024px) {
      :host .header-search__no-result-container > .header-search__list-container {
        display: flex;
      }
      :host .header-search__no-result > .header-search__list-container {
        display: none;
      }
    }

    @media screen and (max-width: 749px) {
      :host {
        display: block;
        margin-left: calc(0px - var(--nova-space-md));
        margin-right: calc(0px - var(--nova-space-md));
      }
      :host h3,
      .header-search__collection-list ul {
        padding: 0 var(--nova-space-md);
      }
      :host .header-search__product-slide-inner-container {
        padding: 0 var(--nova-space-md);
        width: calc(
          200px * var(--card-counts, 0) +
          var(--space-counts, 0) * var(--nova-space-sm)
        );
      }
      :host .header-search__product-card {
        min-width: 200px;
      }
    }

    .product-card__link.h6 {
      font: var(--nova-h6-font);
      letter-spacing: var(--nova-h6-letter-spacing);
      text-transform: var(--nova-h6-text-transform);
    }

    /* NOTE: Copied over shared utility classes for shadow DOM */
    .absolute {
      position: absolute;
    }

    .inset {
      inset: 0;
    }

    .c-transparent {
      color: transparent !important;
    }

    .visually-hidden {
      visibility: hidden;
      position: absolute;
      height: 1px;
      width: 1px;
      overflow: hidden;
    }

    .product-card__wishlist.swym-button.swym-add-to-wishlist-view-product {
      padding: 0;
      display: flex;
      align-items: center;
      height: auto;
      position: absolute;
      top: var(--nova-space-sm);
      right: var(--nova-space-sm);
      color: var(--nova-color-brand-charcoal);
      background: transparent;
      border: 0;
      cursor: pointer;
    }
  `
}

customElements.define('nova-search-result', NovaSearchResult)
